import React, { useCallback, useEffect, useRef, useState } from "react";
import { useBnbSelector } from "../../../../redux/store";
import useSpinner, { type GetImageSrcType } from "../../../../../../../common-deprecated/hooks/spin/useSpinner";
import * as InteriorSpinView from "./styles/InteriorGalleryStyle";
import SrOnly from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import * as Button from "../../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import Icon from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { useWindowDimensions } from "../../../../../../../common-deprecated/hooks";
import { useBreakpoint } from "../../../../../../../common-deprecated/themes/common";
import useSlider from "../../../../../../../common-deprecated/hooks/useSlider";
import { isRTL } from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { trackCustomizeIntGallery } from "../../../../utils/tracking";
import { BnbLabel } from "../../../../utils/labels";
import { useCarImage } from "../../../../hooks/gradeEngine/useCarImage";
import useGetBuildSpinnerFrame, { ImageType } from "../../../../hooks/shared/spin/useGetBuildSpinnerFrame";

type InteriorGalleryPropsType = {
    isFullscreen?: boolean;
    getByFrame?: boolean;
    gradeId?: string;
    bodyTypeId?: string;
};

const InteriorGallery = (props: InteriorGalleryPropsType): JSX.Element => {
    const { isFullscreen, getByFrame, gradeId, bodyTypeId } = props;
    const viewPortRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const windowWidth = useWindowDimensions().width;
    const [viewPortWidth, setViewPortWidth] = useState<number>(0);
    const [activeImage, setActiveImage] = useState<number>(0);

    // Slider functionality should only be enabled when the view is mobile.
    // Note that useBreakpoint usually should be avoided, but in this case it seems like the best solution as the server side render should be correct in any use case.
    const isMobile = useBreakpoint("down", "sm");

    const step = useBnbSelector((state) => state.navigation.step);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const model = useBnbSelector((state) => state.model);
    const getFrameProps = {
        modelId: model.id,
        configVersion: model.configVersion,
    };
    const showMergedModelStep = useBnbSelector((state) => state.bnbSettings.showMergedModelStep);
    const { getFrame } = useCarImage();
    const { getImage, cacheKey: getImageCacheKey } = useGetBuildSpinnerFrame(false, ImageType.InteriorGallery);
    const cacheKey = !getByFrame ? getImageCacheKey : `${model.defaultCarId}-${model.id}-${model.configVersion}`;
    const getImageSrc = useCallback(
        (frame: number): GetImageSrcType =>
            getByFrame && gradeId && bodyTypeId
                ? {
                      src: getFrame({ width: 1230, height: 547, frame, gradeId, bodyTypeId, ...getFrameProps }),
                      srcSet: `${getFrame({
                          width: 1230,
                          height: 547,
                          frame,
                          gradeId,
                          bodyTypeId,
                          ...getFrameProps,
                      })} 1x, ${getFrame({
                          width: 2460,
                          height: 1094,
                          frame,
                          gradeId,
                          bodyTypeId,
                          ...getFrameProps,
                      })} 2x`,
                      sizes: "",
                  }
                : {
                      src: getImage(1230, 547, frame),
                      srcSet: `${getImage(1230, 547, frame)} 1x, ${getImage(2460, 1094, frame)} 2x`,
                      sizes: "",
                  },
        [cacheKey],
    );

    const frameProps = useSpinner(getImageSrc, cacheKey, 0, 3);
    const { imageCache } = frameProps;

    useEffect(() => {
        if (viewPortRef.current && sliderRef.current) {
            const { width } = viewPortRef.current.getBoundingClientRect();
            setViewPortWidth(width);
        }
    }, [viewPortWidth, windowWidth, isFullscreen]);

    const sliderProps = useSlider(
        imageCache.current.length * viewPortWidth,
        viewPortWidth,
        viewPortWidth,
        [],
        isRTL(commonSettings),
    );
    const { startMouseDrag, startTouchDrag, goToPage, sliderX, enableDrag, isScrolling } = sliderProps;
    const { leftArrowClickPage, rightArrowClickPage, leftArrowEnabled, rightArrowEnabled } = sliderProps;

    // Save the active image in state, based on calculating the slider position.
    useEffect(() => {
        const activeIndex =
            sliderX === 0
                ? 0
                : Math.round(
                      Math.abs(
                          sliderX /
                              (((imageCache.current.length || 1) * viewPortWidth) / (imageCache.current.length || 1)),
                      ),
                  );
        if (activeIndex !== activeImage) setActiveImage(activeIndex);
    }, [sliderX, activeImage]);

    // Build images array. Animate and position the images by setting margins and css transitions.
    const images = imageCache.current.map((image, index) => (
        <div
            style={{
                width: "100%",
            }}
            key={image.frame}
            aria-roledescription={`slide-${index}`}
            id={`slide-${index}`}
            aria-labelledby={`image-${index}`}
        >
            <InteriorSpinView.SpinImage
                alt={`Interior image ${index + 1}`}
                key={cacheKey + image.frame}
                {...getImageSrc(image.frame)}
                id={`image-${index}`}
            />
        </div>
    ));

    return (
        <InteriorSpinView.Wrapper>
            <InteriorSpinView.SpinInterior>
                <InteriorSpinView.SpinGallery
                    role="region"
                    aria-roledescription="carousel"
                    aria-label="interior images"
                    ref={viewPortRef}
                >
                    <InteriorSpinView.SpinImages
                        ref={sliderRef}
                        style={{
                            width: `${imageCache.current.length * 100}%`,
                            transform: `translateX(${sliderX || 0}px)`,
                        }}
                        onTouchStart={enableDrag ? startTouchDrag : undefined}
                        onMouseDown={enableDrag ? startMouseDrag : undefined}
                        onClick={(event) => {
                            if (isScrolling.current) {
                                event.preventDefault();
                                event.stopPropagation();
                            }
                        }}
                        onTransitionEnd={() => {
                            isScrolling.current = false;
                        }}
                        role="group"
                        aria-live="polite"
                    >
                        {images}
                    </InteriorSpinView.SpinImages>
                    <InteriorSpinView.SpinNavButtons role="group" aria-label="slide controls">
                        <Button.Icon
                            type="button"
                            large
                            mr={3}
                            hidden={!leftArrowEnabled || isMobile}
                            onClick={() => {
                                trackCustomizeIntGallery(step, showMergedModelStep);
                                leftArrowClickPage();
                            }}
                            aria-labelledby="button-next-interiorspin"
                            aria-disabled={!leftArrowEnabled || isMobile}
                        >
                            <Icon variant={isRTL(commonSettings) ? "chevronRight" : "chevronLeft"} />
                            <SrOnly id="button-next-interiorspin">
                                <BnbLabel label="bnbA11yButtonSliderNext" />
                            </SrOnly>
                        </Button.Icon>
                        <Button.Icon
                            type="button"
                            large
                            ml={3}
                            hidden={!rightArrowEnabled || isMobile}
                            onClick={() => {
                                trackCustomizeIntGallery(step, showMergedModelStep);
                                rightArrowClickPage();
                            }}
                            aria-labelledby="button-previous-interiorspin"
                            aria-disabled={!rightArrowEnabled || isMobile}
                        >
                            <Icon variant={isRTL(commonSettings) ? "chevronLeft" : "chevronRight"} />
                            <SrOnly id="button-previous-interiorspin">
                                <BnbLabel label="bnbA11yButtonSliderPrevious" />
                            </SrOnly>
                        </Button.Icon>
                    </InteriorSpinView.SpinNavButtons>
                    <InteriorSpinView.SpinNav>
                        {imageCache.current.map((image, index) => (
                            <InteriorSpinView.SpinNavItem
                                isActive={image.frame === activeImage}
                                key={`int-button-${image.frame}`}
                                onClick={() => {
                                    goToPage(image.frame);
                                }}
                                data-slide={index}
                            >
                                <SrOnly>{image.frame + 1}</SrOnly>
                            </InteriorSpinView.SpinNavItem>
                        ))}
                    </InteriorSpinView.SpinNav>
                </InteriorSpinView.SpinGallery>
            </InteriorSpinView.SpinInterior>
        </InteriorSpinView.Wrapper>
    );
};

export default InteriorGallery;
